import type { EntityOptionFieldType } from "@entrilia/shared/api/dictionaryTypes";
import type { AccessPermission, EntriliaProduct, RangeRequestParams } from "@entrilia/shared/api/types";
import type { ApprovalStatus, WorkflowSignalType, WorkflowType } from "@entrilia/shared/api/workflowTypes";
import type { PortalRole } from "./accessTypes";
import type { MemberRole } from "./userManagementTypes";
import type { CitationHighlightRectangle } from "./workflowTypes";

export type FieldIdToValueMap = Record<string, unknown>;

export type FieldIdToLookupReferences = Record<string, LookupObjectReference[]>;

export type FieldsConfidenceMap = Record<string, number>;

export interface FieldCitation {
  sourceDataCatalogId: string;
  highlights: CitationHighlightRectangle[];
}

export type FieldsCitationsMap = Record<string, FieldCitation>;

export interface ObjectWithFieldValuesBase {
  id: string;
  fieldValues: FieldIdToValueMap;
  logoUrl?: string;
  lookupObjects?: FieldIdToLookupReferences;
}

export interface ObjectFieldValues extends ObjectWithFieldValuesBase {
  objectType: string;
  createdAt?: string;
  updateAt?: string;
  name: string;
  lookupObjects: FieldIdToLookupReferences;
  displayValues: ObjectDisplayValues;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  hasPossibleDuplicates?: boolean;
  workflowMetadata?: ObjectWorkflowMetadata[];
}

export interface ObjectFieldValuesDetails extends ObjectFieldValues {
  workflowMetadata: ObjectWorkflowMetadata[];
  approvalMetadata: ObjectApprovalMetadata[];
  locks: ObjectLockDetails[];
  checklistTasks: ChecklistTask[];
  pendingUpdate?: boolean;
}

interface ObjectDisplayValues {
  primaryValue: string;
  secondaryValue?: string;
}

export interface ObjectWorkflowMetadata {
  workflowId: string;
  workflowType: WorkflowType;
  availableUserSignals: WorkflowSignalType[];
  failureMessage?: string;
}

export interface ObjectLockDetails {
  lockSource: ObjectLockSource;
  lockReason: string;
  workflowId?: string;
  canUserBypassLock: boolean;
}

export interface ObjectApprovalMetadata {
  approvalId: string;
  workflowId?: string;
  approvalConfigurationId: string;
  pendingApprovalsFromUserIds?: string[];
}

export type ObjectLockSource = "Workflow" | "Extraction";

export interface ChecklistTask {
  id: string;
  approvalId?: string;
  name: string;
  category?: string;
  description?: string;
  displayOrder: number;
  status: ChecklistTaskStatus;
  completedBy?: ChecklistUserReference;
  completedAt?: string;
  approvers: ChecklistUserReference[];
  pendingApprovalFromUserIds: string[];
  approvalStatus?: ApprovalStatus;
  approvedBy?: ChecklistUserReference;
  approvedAt?: string;
  fieldValues: FieldIdToValueMap;
}

export const allChecklistTaskStatuses = ["ToDo", "Done", "Waived", "NotApplicable"] as const;
export type ChecklistTaskStatus = (typeof allChecklistTaskStatuses)[number];

export interface ChecklistUserReference {
  userId: string;
  name: string;
}

export interface LookupObjectReference {
  objectId: string;
  objectType: string;
  entityType?: string;
  primaryFieldValue: string;
  secondaryFieldValue?: string;
  additionalFieldValues?: LookupAdditionalField[];
}

interface LookupAdditionalField {
  fieldId: string;
  fieldName: string;
  fieldValue: string;
}

export type ObjectTypeToLookupReferences = Record<string, LookupObjectReference[]>;

export type BusinessCentralObjectType = "Entity" | "Contact" | "CommunicationGroup";

export interface SearchObjectsRequest {
  fieldIds?: string[];
  range?: RangeRequestParams;
  includeLookupObjects?: boolean;
  filters?: ObjectFilter[];
  searchTerm?: string;
  sort?: ObjectFieldSort;
}

export interface ExportObjectsToExcelRequest {
  filters?: ObjectFilter[];
  includeApprovalDetails?: boolean;
  includeRelatedObjectType?: string;
  sendEmailNotificationWhenFinished?: boolean;
}

export interface ObjectFieldSort {
  fieldId: string;
  ascending: boolean;
}

export interface CreateObjectRequest {
  fieldValuesUserInput: FieldIdToValueMap;
  entityTypeCode?: string;
}

export interface UpdateObjectRequest {
  fieldValuesUserInput: FieldIdToValueMap;
  relatedObjects?: RelatedObjectsUpdateRequests;
}

export interface RelatedObjectsUpdateRequests {
  updatedObjects: {
    objectType: string;
    objectId: string;
    fieldValuesUserInput: FieldIdToValueMap;
  }[];
  deletedObjects: {
    objectType: string;
    objectId: string;
  }[];
  createdObjects: {
    objectType: string;
    fieldValuesUserInput: FieldIdToValueMap;
    entityTypeCode?: string;
  }[];
}

// Used for Investor, Contact and Fund only
export interface FieldValuesResponse {
  objectId: string;
  values: FieldValue[];
  lookupObjects?: FieldIdToLookupReferences;
}

// Used for Investor, Contact and Fund only
export interface UpdateEntityFieldsRequest {
  values: FieldValue[];
}

interface FieldValue {
  fieldId: string;
  value?: unknown;
}

export interface PageLayout {
  configurationId?: string;
  objectType: string;
  banner?: LayoutBanner;
  fieldGroups: FieldGroup[];
  relatedObjectGrids?: LayoutRelatedObjects[];
}

export interface CreateOrganizationPageLayoutRequest {
  configurationId: string;
  fieldGroups: FieldGroup[];
}

export interface FieldGroup {
  name: string;
  fields: FieldReference[];
}

interface FieldReference {
  fieldId: string;
  isVisible: boolean;
}

export type LayoutBanner = InvoiceLayoutBanner; // | add other types as needed

export interface InvoiceLayoutBanner {
  type: "Invoice";
  fieldMappings: Record<IncoiceBannerFieldKey, string>;
}

type IncoiceBannerFieldKey = "TitleField" | "DueDateField" | "AmountField" | "CurrencyField";

export interface LayoutRelatedObjects {
  objectType: string;
  name: string;
  referenceFieldId: string;
  fieldIds: string[];
  parentSummaryFieldIds: string[];
  sorting?: ObjectFieldSort;
}

export interface ObjectClassDefinitionInfo {
  updateAt?: string;
  objectType: string;
  objectName: string;
  source: ObjectClassDefinitionSource;
  businessCentralType?: BusinessCentralObjectType;
  productAreas: EntriliaProduct[];
  supportedFeatures: ObjectFeature[];
  checklists: ObjectChecklist[];
}

export interface ObjectClassDefinition extends ObjectClassDefinitionInfo {
  fields: Field[];
  defaultGridFieldIds: string[];
  showTimestampsInGridByDefault?: boolean;
  displayFields: ObjectDisplayFields;
  objectContactCategoryType?: string;
  supportedContactRoles: PortalRole[];
  internalUserPermissionsCategoryType?: string;
  supportedInternalUserRoles: MemberRole[];
  predefinedFilters: ObjectPredefinedFilters[];
  duplicateDetection?: ObjectDuplicateDetection;
  workflowActionsWithChecks?: WorkflowSignalType[];
}

interface ObjectDisplayFields {
  primaryFieldId: string;
  secondaryFieldId?: string;
  additionalFieldIds?: string[];
}

export interface ObjectClassDefinitionWithPermissions {
  objectClassDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
}

export interface ObjectClassDefinitionInfoWithPermissions {
  objectClassDefinition: ObjectClassDefinitionInfo;
  objectPermissions: ObjectPermissions;
}

export interface ObjectPermissions {
  objectDataReadPermissions: AccessPermission[];
  objectDataWritePermissions: AccessPermission[];
  filesReadPermissions: AccessPermission[];
  filesWritePermissions: AccessPermission[];
  notesReadPermissions: AccessPermission[];
  notesWritePermissions: AccessPermission[];
  objectContactsReadPermissions: AccessPermission[];
  objectContactsWritePermissions: AccessPermission[];
  objectInternalUsersReadPermissions: AccessPermission[];
  objectInternalUsersWritePermissions: AccessPermission[];
}

export const allObjectFeatures = [
  "Checklists",
  "Notes",
  "Files",
  "PortfolioMetrics",
  "DataImport",
  "Contacts",
  "InternalUserPermissions",
  "CardFilePreview",
] as const;

export type ObjectFeature = (typeof allObjectFeatures)[number];

export type ObjectClassDefinitionSource = "System" | "Custom";

export interface ObjectInfo {
  objectType: string;
  objectId: string;
  entityTypeCode?: string;
}

export interface ObjectChecklist {
  id: string;
  name: string;
  conditions: ObjectChecklistCondition[];
  taskTemplateIds: string[]; // referenced object type: "ChecklistTaskTemplate"
}

export interface ObjectChecklistCondition {
  objectType: string;
  field: ObjectFilter;
}

export interface AddChecklistRequest {
  name: string;
  conditions: ObjectChecklistCondition[];
  taskTemplateIds: string[];
}

export interface UpdateChecklistRequest {
  name?: string;
  conditions: ObjectChecklistCondition[];
  taskTemplateIds: string[];
}

export interface UpdateObjectDefinitionRequest {
  name?: string;
  supportedFeatures?: ObjectFeature[];
}

export interface SearchObjectDefinitionsRequest {
  supportedFeatures?: ObjectFeature[];
}

// Object Fields

export interface CreateFieldRequest {
  name: string;
  type: EntityFieldType;
  configuration?: EntityFieldConfiguration;
}

export interface AddFieldToObjectClassDefinitionResponse {
  objectClassDefinition: ObjectClassDefinition;
  field: Field;
}

export interface UpdateFieldRequest {
  name: string;
  configuration?: EntityFieldConfiguration;
}

export interface Field {
  id: string;
  type: EntityFieldType;
  name: string;
  source: FieldSource;
  configuration?: EntityFieldConfiguration;
  attributes: FieldAttribute[];
}

export type FieldAttribute =
  | "ObjectIdentifier"
  | "Required"
  | "ReadOnly"
  | "Sortable"
  | "Searchable"
  | "HiddenByDefault"
  | "ExcludedFromApprovalRules";

export type FieldSource = "BusinessCentral" | "Portal" | "UserDefined";

export interface ObjectLogoResponse {
  logoUrl: string;
}

export interface ValidateUniqueFieldRequest {
  fieldId: string;
  value: unknown;
}

export type EntityFieldType =
  | "Text"
  | "Email"
  | "Phone"
  | "Url"
  | "Number"
  | "Money"
  | "Percent"
  | "Select"
  | "MultiSelect"
  | "UserDefinedOptionsSelect"
  | "UserDefinedOptionsMultiSelect"
  | "Date"
  | "DateTime"
  | "Checkbox"
  | "Lookup"
  | "MultiLookup"
  | "Guid";

export type LayoutPage = "Card";

export type FieldConfigurationType =
  "Text" | "Percent" | "Number" | "Money" | "Date" | "Select" | "UserDefinedOptionsSelect" | "Lookup";

type BaseEntityFieldConfiguration = {
  $type: FieldConfigurationType;
};

export type EntityFieldConfiguration =
  | TextEntityFieldConfiguration
  | PercentEntityFieldConfiguration
  | UserDefinedOptionsEntityFieldConfiguration
  | OptionsEntityFieldConfiguration
  | NumberEntityFieldConfiguration
  | DateEntityFieldConfiguration
  | CurrencyEntityFieldConfiguration
  | LookupFieldConfiguration;

export interface TextEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Text";
  maxLength?: number;
}

export interface PercentEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Percent";
  precision?: number;
}

export interface OptionsEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Select";
  fieldType: EntityOptionFieldType;
  allowNewOptions: boolean;
  maxNewOptionLength?: number;
}

export const allNumberScaleFormats = ["Thousands", "Millions", "Billions", "Multiple"] as const;
export type NumberScaleFormat = (typeof allNumberScaleFormats)[number];

export interface NumberEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Number";
  precision?: number;
  scaleFormat?: NumberScaleFormat;
}

export interface DateEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Date";
  dateComponents?: DateComponent[];
}

export type DateComponent = "Year" | "Month" | "Day";

export interface CurrencyEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Money";
  currencyCode?: string;
  currencyCodeFieldId?: string;
  precision?: number;
  scaleFormat?: NumberScaleFormat;
}

export interface UserDefinedOptionsEntityFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "UserDefinedOptionsSelect";
  allowNewOptions: boolean;
  userDefinedOptions: SelectOption[];
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface MultiLabelSelectOption extends SelectOption {
  secondaryLabel?: string;
}

export interface LookupFieldConfiguration extends BaseEntityFieldConfiguration {
  $type: "Lookup";
  objectType: string;
  allowCreateNewObjects?: boolean;
  newObjectEntityType?: string;
  relatedObjectFilters: RelatedObjectFilter[];
  displayColumns?: LookupDisplayColumn[];
}

interface LookupDisplayColumn {
  additionalFieldId: string;
  name: string;
}

export interface RelatedObjectFilter {
  relatedObjectType: string;
  relatedObjectFieldId: string; // This is not used yet, we filter for all related object fields of comparisonObjectType
  comparisonObjectType?: string;
  comparisonObjectFieldId?: string;
  lookupObjectComparisonFieldId: string;
}

// Object Filters

export interface ObjectFilter {
  fieldId: string;
  textFilter?: TextObjectFilter;
  numberFilter?: NumberObjectFilter;
  dateFilter?: DateObjectFilter;
  booleanFilter?: BooleanObjectFilter;
  multiSelectFilter?: MultiSelectObjectFilter;
  guidFilter?: GuidFilter;
}

export interface TextObjectFilter {
  operator: TextObjectFilterOperator;
  value?: string;
  values?: string[];
}

export interface NumberObjectFilter {
  operator: NumberObjectFilterOperator;
  value?: number;
  from?: number;
  to?: number;
}

export interface DateObjectFilter {
  operator: DateObjectFilterOperator;
  from?: string;
  to?: string;
}

export interface BooleanObjectFilter {
  value: boolean;
}

export interface MultiSelectObjectFilter {
  operator: MultiSelectObjectFilterOperator;
  selectedValues?: string[];
}

export interface GuidFilter {
  value: string;
  values?: string[];
}

export const allTextObjectFilterOperators = [
  "Contains",
  "DoesNotContain",
  "EqualsCaseInsensitive",
  "InArray",
  "Empty",
  "NotEmpty",
] as const;

export type TextObjectFilterOperator = (typeof allTextObjectFilterOperators)[number];

export const allNumberObjectFilterOperators = [
  "Equals",
  "NotEquals",
  "Between",
  "GreaterThan",
  "GreaterThanOrEquals",
  "LessThan",
  "LessThanOrEquals",
  "Empty",
  "NotEmpty",
] as const;

export type NumberObjectFilterOperator = (typeof allNumberObjectFilterOperators)[number];

export const allDateObjectFilterOperators = ["Range", "Empty", "NotEmpty"] as const;

export type DateObjectFilterOperator = (typeof allDateObjectFilterOperators)[number];

export const allMultiSelectObjectFilterOperators = ["OneOf", "NotOneOf", "Empty", "NotEmpty"] as const;

export type MultiSelectObjectFilterOperator = (typeof allMultiSelectObjectFilterOperators)[number];

export interface ObjectPredefinedFilters {
  filterName: string;
  includeInCounters: boolean;
  filters: ObjectFilter[];
}

interface ObjectDuplicateRule {
  description: string;
  fieldIds: string[];
}

interface ObjectDuplicateDetection {
  candidateFilters: ObjectFilter[];
  rulesStrongestFirst: ObjectDuplicateRule[];
  summaryFieldIds: string[];
}

export interface ObjectDuplicate {
  objectId?: string;
  matchDescription: string;
  summaryValues: unknown[];
}

export interface ObjectDuplicatesResponse {
  duplicates: ObjectDuplicate[];
  detailsHidden: boolean;
}

export interface ObjectCounters {
  countsByFilter: ObjectCount[];
}

interface ObjectCount {
  filterName: string;
  count: number;
}

// Object Contacts

export interface ObjectContactInfo {
  id: string;
  name: string;
  email: string;
}

export interface CreateObjectContactRequest {
  name: string;
  email: string;
}

export interface ChangeChecklistTaskStatusesRequest {
  tasks: { taskId: string; status: ChecklistTaskStatus }[];
}

export interface UpdateChecklistTaskDetailsRequest {
  taskId: string;
  descriptionUserInput?: string;
  fieldValuesUserInput: FieldIdToValueMap;
}

export interface ObjectContact {
  contactId: string;
  role?: PortalRole;
  categories: string[];
  registrationDate?: string;
}

export interface UpdateObjectContactsRequest {
  contacts: ObjectContactRequest[];
}

export interface ObjectContactRequest {
  contactId: string;
  role?: PortalRole;
  categories: string[];
}

export interface ObjectInternalUserPermissions {
  userId: string;
  categories: string[];
}

export interface UpdateObjectInternalUserPermissionsRequest {
  userPermissions: ObjectInternalUserPermissionsRequest[];
}

export interface ObjectInternalUserPermissionsRequest {
  userId: string;
  categories: string[];
}

export interface ObjectInternalUser {
  id: string;
  name: string;
  email: string;
}

export interface ObjectAccessCategory {
  id: string;
  name: string;
  type: string;
}

export interface CreateObjectAccessCategoryRequest {
  objectType: string;
  name: string;
  type: string;
}

export interface UpdateObjectAccessCategoryRequest {
  objectType: string;
  name: string;
}

export interface GetRelatedObjectsRequest {
  relatedObjects: RelatedObjectSpecification[];
  includeLookupOptions?: boolean;
}

interface RelatedObjectSpecification {
  objectType: string;
  fieldIds: string[];
}

export interface GetRelatedObjectsResponse {
  relatedObjectsByType: RelatedObjectsResponse[];
}

export interface RelatedObjectsResponse {
  objectType: string;
  objectClassDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  relatedObjects: RelatedObject[];
  lookupOptions?: ObjectTypeToLookupReferences;
}

export interface RelatedObject {
  id: string;
  objectType: string;
  createdAt?: string;
  updateAt?: string;
  name: string;
  fieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects: FieldIdToLookupReferences;
  displayValues: ObjectDisplayValues;
}
